import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { createApiClient, getTimezoneOffsetString, getLocalTimestamp } from "../../../api/apiClient";
import type { CreateNotificationFormState } from "../components/CreateNotificationModal";
import { formatDateWithTimezone } from "../util/dateFormatter";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchActiveNotifications } from "../../../store/thunks/ticketingPortalThunk";
import type { TicketingNotificationApiItem } from "../../../store/slices/ticketingPortalSlice";

// ─── Backend API Types ──────────────────────────────────────────────────────

interface TktNotificationResponse {
  notificationId: number;
  type: string;
  title: string;
  /** API returns both `message` (primary) and `description` (legacy fallback). */
  message?: string;
  description?: string;
  displayUntilClosed: boolean;
  startDateTime?: string;
  endDateTime?: string;
  severity?: string;
  isWidespread?: boolean;
  widespread?: boolean;
  /** API returns `relatedTicketId`; legacy field was `ticketNumber`. */
  relatedTicketId?: string;
  ticketNumber?: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt?: string;
  audienceTargetType?: string;
  targetValues?: {
    regions?: string[];
    markets?: string[];
    locations?: string[];
    roles?: string[];
    systems?: string[];
  };
  active?: boolean;
  allUsers?: boolean;
  includeActionButton?: boolean;
  triggerTeamsNotification?: boolean;
  triggerBannerNotification?: boolean;
  triggerEmailNotification?: boolean;
  estimatedUserCount?: number;
  notificationTriggered?: boolean;
  draft?: boolean;
}

// ─── Frontend Types ─────────────────────────────────────────────────────────

export type NotificationType = "incident" | "maint" | "general";
export type NotificationStatusTab = "active" | "scheduled" | "sent" | "draft";
export type NotificationReadStatus = "read" | "unread";
export type MessageAuthorType = "user" | "system" | "sustain" | "servicenow";

export interface NotificationStats {
  notified: number;
  seen?: number;
  attached?: number;
  recipients?: number;
}

export interface PortalNotification {
  id: string;
  /** Numeric backend ID — present for API-backed notifications, absent for local drafts. */
  backendId?: number;
  type: NotificationType;
  statusTab: NotificationStatusTab;
  readStatus: NotificationReadStatus;
  title: string;
  message: string;
  locations: string;
  startedAt: string;
  linkedTicket?: string;
  stats: NotificationStats;
  isHomepageBanner?: boolean;
  /** If true, render "I'm Affected Too" button on homepage banner (requires linkedTicket). */
  includeActionButton?: boolean;
  /** Whether to trigger Teams notification. */
  triggerTeamsNotification?: boolean;
  /** Whether to trigger email notification. */
  triggerEmailNotification?: boolean;
  /** Whether this notification is a draft. */
  draft?: boolean;
  /** "YYYY-MM-DDTHH:mm" — raw start datetime for edit form reconstruction. */
  startDate?: string;
  /** ISO "YYYY-MM-DDTHH:mm" — banner auto-deactivates after this time. Omit or leave empty for no expiry. */
  endDate?: string;
  /** Whether this notification displays until the linked ticket is closed. */
  displayUntilClosed?: boolean;
  /** Backend audience targeting type — used for edit form reconstruction. */
  audienceTargetType?: string;
  /** Backend audience targeting values — used for edit form reconstruction. */
  targetValues?: {
    regions?: string[];
    markets?: string[];
    locations?: string[];
    roles?: string[];
    systems?: string[];
  };
}

export interface CommMessage {
  id: string;
  senderName: string;
  senderInitials: string;
  senderRole: string;
  content: string;
  timestamp: string;
  type: MessageAuthorType;
  avatarColor?: string;
  attachmentName?: string;
}

// ─── Mock Data ──────────────────────────────────────────────────────────────

const INITIAL_NOTIFICATIONS: PortalNotification[] = [
  {
    id: "notif-001",
    type: "incident",
    statusTab: "active",
    readStatus: "read",
    title: "Widespread Incident: EWM Authentication Outage",
    message:
      "14 locations reporting SAP authentication service unavailable. Workaround: manual slip picklist. ETA for resolution: 11:00 AM IST.",
    locations: "SoCal Market · 14 locations",
    startedAt: "Started Apr 17, 2026, 06:58 AM IST",
    linkedTicket: "NTI-2026-00421",
    stats: { notified: 184, seen: 148, attached: 22 },
    isHomepageBanner: true,
    includeActionButton: true,
  },
  {
    id: "notif-002",
    type: "maint",
    statusTab: "draft",
    readStatus: "unread",
    title: "Planned Maintenance: Navigator Analytics refresh delay",
    message:
      "Scheduled semantic layer refresh will delay reporting dashboards by up to 2 hours tonight. KPIs remain available via native Navigator charts. No action required.",
    locations: "All Users · North America",
    startedAt: "Scheduled Apr 22, 2026, 02:00 AM IST",
    stats: { notified: 2847, recipients: 2847 },
    isHomepageBanner: false,
  },
  {
    id: "notif-003",
    type: "general",
    statusTab: "active",
    readStatus: "unread",
    title: "New Feature: Sustain Team Queue now supports bulk assign",
    message:
      "You can now select multiple tickets in the Sustain Queue and assign them to a team member in one action. Click the checkbox column to get started.",
    locations: "Role: Sustain Team",
    startedAt: "Apr 19, 2026, 09:00 AM IST",
    stats: { notified: 8, seen: 7 },
    isHomepageBanner: false,
  },
  {
    id: "notif-004",
    type: "maint",
    statusTab: "scheduled",
    readStatus: "unread",
    title: "Scheduled Downtime: Navigator build deployment",
    message:
      "Navigator will be briefly unavailable (approx. 10 min) on 04/25 between 2:00–3:00 AM IST for a platform update. Sessions will auto-resume after restart.",
    locations: "All Users · North America",
    startedAt: "Scheduled Apr 25, 2026, 02:00 AM IST",
    stats: { recipients: 2847, notified: 2847 },
    isHomepageBanner: false,
  },
  {
    id: "notif-005",
    type: "incident",
    statusTab: "sent",
    readStatus: "read",
    title: "Resolved: Voice Pick headset pairing dropouts in Zone 4",
    message:
      "IT confirmed driver update has been pushed to all affected devices. Zone 4 Voice Pick operations are fully restored. Close-out INC-4820812.",
    locations: "SoCal Market · 6 locations",
    startedAt: "Resolved Apr 15, 2026, 03:45 PM IST",
    linkedTicket: "NTI-2026-00418",
    stats: { notified: 72, seen: 68 },
    isHomepageBanner: false,
  },
  {
    id: "notif-006",
    type: "general",
    statusTab: "sent",
    readStatus: "read",
    title: "Reminder: Q2 Racking Inspection window opens next week",
    message:
      "All locations must complete Q2 racking inspections between 04/28–05/05. Use the Navigator racking inspection module. Contact your regional lead with questions.",
    locations: "All Locations · North America",
    startedAt: "Apr 21, 2026, 08:00 AM IST",
    stats: { notified: 1243, seen: 891 },
    isHomepageBanner: false,
  },
];

const INITIAL_MESSAGES: CommMessage[] = [
  {
    id: "msg-001",
    senderName: "Alex Rivera",
    senderInitials: "AR",
    senderRole: "Submitter",
    content:
      "Submitted ticket. Please see the attached screenshot of the error message.",
    timestamp: "04/17 6:42 AM",
    type: "user",
    avatarColor: "#005CBC",
  },
  {
    id: "msg-002",
    senderName: "Auto-assigned",
    senderInitials: "✓",
    senderRole: "System",
    content: "Routed to Sustain Queue. Category: System Issue → EWM/SAP.",
    timestamp: "04/17 6:43 AM",
    type: "system",
    avatarColor: "#838383",
  },
  {
    id: "msg-003",
    senderName: "Maria Chen",
    senderInitials: "MC",
    senderRole: "Sustain Team Lead",
    content:
      "Confirming we see the same behavior on our test devices. Escalating to IT via ServiceNow now. Expect an INC link in the next few minutes.",
    timestamp: "04/17 6:58 AM",
    type: "sustain",
    avatarColor: "#7871E2",
  },
  {
    id: "msg-004",
    senderName: "ServiceNow",
    senderInitials: "SN",
    senderRole: "Integration",
    content:
      "INC-4820739 created and linked. Status: New → In Progress. Assigned to Corporate IT · SAP Basis.",
    timestamp: "04/17 7:01 AM",
    type: "servicenow",
    avatarColor: "#0078A7",
  },
  {
    id: "msg-005",
    senderName: "13 locations attached",
    senderInitials: "14",
    senderRole: "System",
    content:
      "Impact scope expanded to 14 sites. Auto-escalated to widespread incident banner.",
    timestamp: "04/17 7:40 AM",
    type: "system",
    avatarColor: "#838383",
  },
  {
    id: "msg-006",
    senderName: "Sarah Nguyen",
    senderInitials: "SN",
    senderRole: "From ServiceNow",
    content:
      "Identified the issue. A certificate rotation on the SAP auth service Sunday 04/20 did not propagate to the SoCal gateway cluster. Pushing the cert now. Expect resolution by 11 AM PT.",
    timestamp: "04/21 8:12 AM",
    type: "servicenow",
    avatarColor: "#0078A7",
  },
  {
    id: "msg-007",
    senderName: "Maria Chen",
    senderInitials: "MC",
    senderRole: "Sustain Team Lead",
    content:
      "Thanks Sarah. I'll push the update to the landing banner and notify the affected sites via Notifications & Comms. Will monitor for resolution confirmation.",
    timestamp: "04/21 8:30 AM",
    type: "sustain",
    avatarColor: "#7871E2",
  },
  {
    id: "msg-008",
    senderName: "Notification sent",
    senderInitials: "🔔",
    senderRole: "System",
    content: "Status update pushed to 14 affected locations and homepage banner.",
    timestamp: "04/21 8:34 AM",
    type: "system",
    avatarColor: "#838383",
  },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

const NOTIF_TYPE_MAP: Record<string, NotificationType> = {
  "Active Incident": "incident",
  "Widespread Incident": "incident",
  "Scheduled Maintenance": "maint",
  "General Announcement": "general",
};

const SEVERITY_MAP: Record<string, string> = {
  Critical: "Critical",
  High: "High",
  Medium: "Medium",
  Standard: "Medium",
  Low: "Low",
};

const pad = (x: number) => String(x).padStart(2, "0");

/** Derives a human-readable audience label from API audience fields. */
function deriveLocationsLabel(n: TktNotificationResponse): string {
  const type = n.audienceTargetType ?? "ALL_USERS";
  const tv = n.targetValues ?? {};
  if (type === "ALL_USERS" || n.allUsers) return "All Users";

  const parts: string[] = [];
  const types = type.split(",").map((t) => t.trim());

  if (types.includes("BY_REGION") && tv.regions?.length)
    parts.push(`By Region · ${tv.regions.length} selected`);
  if (types.includes("BY_MARKET") && tv.markets?.length)
    parts.push(
      tv.markets.length <= 2 ? tv.markets.join(" · ") : `By Market · ${tv.markets.length} selected`
    );
  if (types.includes("BY_LOCATION") && tv.locations?.length)
    parts.push(
      tv.locations.length <= 2 ? tv.locations.join(" · ") : `By Location · ${tv.locations.length} locations`
    );
  if (types.includes("BY_ROLE") && tv.roles?.length)
    parts.push(`By Role · ${tv.roles.join(", ")}`);
  if (types.includes("BY_TARGET_SYSTEM") && tv.systems?.length)
    parts.push(`By System · ${tv.systems.join(", ")}`);

  return parts.length > 0 ? parts.join(" | ") : type.replace(/_/g, " ").replace(/\bBY\b/g, "By").trim();
}

const hasTimezoneDesignator = (value: string): boolean => /[Z]$|[+-]\d{2}:?\d{2}$/.test(value.trim());

const toUtcIsoString = (value: string): string => {
  const trimmed = value.trim();
  return hasTimezoneDesignator(trimmed) ? trimmed : `${trimmed}Z`;
};

const parseApiDateAsUtc = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(toUtcIsoString(value));
  return isNaN(parsed.getTime()) ? null : parsed;
};

function mapApiResponse(n: TktNotificationResponse): PortalNotification {
  const now = new Date();
  const start = parseApiDateAsUtc(n.startDateTime);
  const end = parseApiDateAsUtc(n.endDateTime);

  let statusTab: NotificationStatusTab = "active";
  // If draft is true, always show in draft section
  if (n.draft) {
    statusTab = "draft";
  } else if (!n.displayUntilClosed) {
    if (start && start > now) statusTab = "scheduled";
    else if (end && end < now) statusTab = "sent";
  }

  let startedAt = "";
  if (n.startDateTime) {
    const formattedDate = formatDateWithTimezone(n.startDateTime);
    startedAt = statusTab === "scheduled" ? `Scheduled ${formattedDate}` : `Sent ${formattedDate}`;
  } else if (n.displayUntilClosed && n.createdAt) {
    const formattedDate = formatDateWithTimezone(n.createdAt);
    startedAt = `Started ${formattedDate}`;
  }

  return {
    id: String(n.notificationId),
    backendId: n.notificationId,
    type: NOTIF_TYPE_MAP[n.type] ?? "general",
    statusTab,
    readStatus: "unread",
    title: n.title,
    // API returns `message`; `description` kept as legacy fallback
    message: n.message ?? n.description ?? "",
    locations: deriveLocationsLabel(n),
    startedAt,
    // API returns `relatedTicketId`; `ticketNumber` kept as legacy fallback
    linkedTicket: n.relatedTicketId ?? n.ticketNumber,
    stats: { notified: n.estimatedUserCount ?? 0 },
    isHomepageBanner: n.triggerBannerNotification ?? false,
    includeActionButton: n.includeActionButton ?? false,
    triggerTeamsNotification: n.triggerTeamsNotification ?? false,
    triggerEmailNotification: n.triggerEmailNotification ?? false,
    draft: n.draft ?? false,
    displayUntilClosed: n.displayUntilClosed,
    // Normalise to "YYYY-MM-DDTHH:mm" so datetime-local inputs render correctly
    startDate: toDatetimeLocal(n.startDateTime),
    endDate: toDatetimeLocal(n.endDateTime),
    audienceTargetType: n.audienceTargetType,
    targetValues: n.targetValues || { regions: [], markets: [], locations: [], roles: [], systems: [] },
  };
}

// toIso function removed — backend handles timezone conversion
// Send datetime-local strings (YYYY-MM-DDTHH:mm) directly without UTC conversion

/**
 * Normalise any ISO datetime string to the "YYYY-MM-DDTHH:mm" format required
 * by datetime-local inputs.  Strips seconds/milliseconds/timezone suffix.
 */
function toDatetimeLocal(iso: string | undefined): string {
  if (!iso) return "";
  const d = parseApiDateAsUtc(iso);
  if (!d) return "";
  if (isNaN(d.getTime())) return "";
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * Map CreateNotificationFormState audienceTypes array to comma-separated API
 * audienceTargetType string and build the targetValues object.
 */
function buildAudienceTargeting(formData: CreateNotificationFormState) {
  const audienceTypeMap: Record<string, string> = {
    "All Users": "ALL_USERS",
    "By Region": "BY_REGION",
    "By Market": "BY_MARKET",
    "By Location": "BY_LOCATION",
    "By Role": "BY_ROLE",
    "By Target System": "BY_TARGET_SYSTEM",
  };

  const audienceTypes = Array.isArray((formData as any).audienceTypes)
    ? (formData as any).audienceTypes as string[]
    : [(formData as any).audienceType ?? "All Users"];

  const audienceTargetType =
    audienceTypes.length > 0
      ? audienceTypes.map((t) => audienceTypeMap[t] ?? "ALL_USERS").join(",")
      : "ALL_USERS";

  // Build targetValues — include whichever arrays have values
  const targetValues: Record<string, string[]> = {};
  if (Array.isArray(formData.regions)       && formData.regions.length > 0)       targetValues.regions   = formData.regions;
  if (Array.isArray(formData.markets)       && formData.markets.length > 0)       targetValues.markets   = formData.markets;
  if (Array.isArray(formData.locations)     && formData.locations.length > 0)     targetValues.locations = formData.locations;
  if (Array.isArray(formData.roles)         && formData.roles.length > 0)         targetValues.roles     = formData.roles;
  if (Array.isArray(formData.targetSystems) && formData.targetSystems.length > 0) targetValues.systems   = formData.targetSystems;

  return { audienceTargetType, targetValues };
}

/** Estimate the number of recipients for the given form state. */
function estimateRecipients(formData: CreateNotificationFormState): number {
  const ROLE_COUNTS: Record<string, number> = {
    "Sustain Team": 8, "Field User": 1950, "Warehouse Manager": 124, "System Admin": 23, "IT Support": 45,
  };
  const audienceTypes = Array.isArray((formData as any).audienceTypes)
    ? (formData as any).audienceTypes as string[]
    : [(formData as any).audienceType ?? "All Users"];
  if (audienceTypes.includes("All Users")) return 2847;
  let total = 0;
  if (audienceTypes.includes("By Region"))        total += (formData.regions?.length ?? 0) * 520;
  if (audienceTypes.includes("By Market"))        total += (formData.markets?.length ?? 0) * 210;
  if (audienceTypes.includes("By Location"))      total += (formData.locations?.length ?? 0) * 37;
  if (audienceTypes.includes("By Role"))          total += (formData.roles ?? []).reduce((s, r) => s + (ROLE_COUNTS[r] ?? 30), 0);
  if (audienceTypes.includes("By Target System")) total += (formData.targetSystems?.length ?? 0) * 340;
  return total;
}

// ─── Hook ───────────────────────────────────────────────────────────────────

const TABS: { key: NotificationStatusTab; label: string }[] = [
  { key: "active", label: "Active" },
  { key: "scheduled", label: "Scheduled" },
  { key: "sent", label: "Sent" },
  { key: "draft", label: "Drafts" },
];

const useNotificationsComms = () => {
  const dispatch = useDispatch<AppDispatch>();
  const notificationsApiCache = useSelector(
    (state: RootState) => state.ticketingPortal.notificationsApiCache
  );
  const notificationsApiFetched = useSelector(
    (state: RootState) => state.ticketingPortal.notificationsApiFetched
  );
  const [notifications, setNotifications] = useState<PortalNotification[]>([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [messages, setMessages] = useState<CommMessage[]>(INITIAL_MESSAGES);
  const [activeTab, setActiveTab] = useState<NotificationStatusTab>("active");
  const [readFilter, setReadFilter] = useState<"all" | "read" | "unread">("all");
  const [inputMessage, setInputMessage] = useState("");
  const [attachPending, setAttachPending] = useState(false);

  // Ref mirrors notifications for use in async callbacks without stale closure issues
  const notificationsRef = useRef<PortalNotification[]>([]);
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  // ── Hydrate local list from shared Redux cache ────────────────────────
  useEffect(() => {
    if (!notificationsApiFetched) return;
    const apiNotifs = (notificationsApiCache as TicketingNotificationApiItem[]).map(
      (n) => mapApiResponse(n as TktNotificationResponse)
    );
    setNotifications((prev) => {
      // Preserve local drafts (no backendId) and replace API-backed entries
      const drafts = prev.filter((n) => !n.backendId);
      return [...drafts, ...apiNotifs];
    });
  }, [notificationsApiCache, notificationsApiFetched]);

  // ── Fetch all notifications via shared thunk when cache is empty ──────
  const fetchAll = useCallback(async () => {
    if (notificationsApiFetched) return;
    setIsLoadingNotifications(true);
    try {
      await dispatch(fetchActiveNotifications() as any);
    } catch (err) {
      console.error("[useNotificationsComms] fetchAll failed:", err);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [dispatch, notificationsApiFetched]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ── Derived: tab counts ────────────────────────────────────────────────
  const tabCounts = useMemo<Record<NotificationStatusTab, number>>(() => {
    const counts = { active: 0, scheduled: 0, sent: 0, draft: 0 };
    notifications.forEach((n) => {
      counts[n.statusTab] = (counts[n.statusTab] || 0) + 1;
    });
    return counts;
  }, [notifications]);

  // ── Derived: unread badge count (active tab only) ──────────────────────
  const unreadCount = useMemo(
    () => notifications.filter((n) => n.statusTab === "active" && n.readStatus === "unread").length,
    [notifications]
  );

  // ── Derived: filtered list ─────────────────────────────────────────────
  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      if (n.statusTab !== activeTab) return false;
      if (readFilter === "read" && n.readStatus !== "read") return false;
      if (readFilter === "unread" && n.readStatus !== "unread") return false;
      return true;
    });
  }, [notifications, activeTab, readFilter]);

  // ── Actions ────────────────────────────────────────────────────────────
  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readStatus: "read" } : n))
    );
  }, []);

  const toggleReadStatus = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id
          ? { ...n, readStatus: n.readStatus === "read" ? "unread" : "read" }
          : n
      )
    );
  }, []);

  /** Deactivates (soft-deletes) API-backed notifications; removes local drafts immediately. */
  const deleteNotification = useCallback((id: string) => {
    const notif = notificationsRef.current.find((n) => n.id === id);
    // Optimistic removal
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (notif?.backendId) {
      const api = createApiClient();
      api.put(`/ticketing/notifications/${notif.backendId}/deactivate`).catch((err) => {
        console.error("[useNotificationsComms] deactivate failed:", err);
        // Restore on failure
        setNotifications((prev) => [notif, ...prev.filter((n) => n.id !== id)]);
      });
    }
  }, []);

  const sendMessage = useCallback(
    (authorName = "Maria Chen", authorInitials = "MC", role = "Sustain Team Lead") => {
      const trimmed = inputMessage.trim();
      if (!trimmed) return;
      const newMsg: CommMessage = {
        id: `msg-${Date.now()}`,
        senderName: authorName,
        senderInitials: authorInitials,
        senderRole: role,
        content: trimmed,
        timestamp: new Date().toLocaleString("en-US", {
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
        type: "sustain",
        avatarColor: "#7871E2",
        attachmentName: attachPending ? "attachment.png" : undefined,
      };
      setMessages((prev) => [...prev, newMsg]);
      setInputMessage("");
      setAttachPending(false);
    },
    [inputMessage, attachPending]
  );

  const markAllRead = useCallback(() => {
    setNotifications((prev) =>
      prev.map((n) => (n.statusTab === activeTab ? { ...n, readStatus: "read" } : n))
    );
  }, [activeTab]);

  /** Add a local draft (not sent to backend). */
  const addNotification = useCallback((notif: PortalNotification) => {
    setNotifications((prev) => [notif, ...prev]);
  }, []);

  /** Update local fields (used for drafts and for read-status changes). */
  const updateNotification = useCallback(
    (id: string, updates: Partial<Omit<PortalNotification, "id">>) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, ...updates } : n))
      );
    },
    []
  );

  /**
   * Create a new notification via the backend API.
   * The caller provides pre-computed display fields (`displayFields`) that will be
   * merged onto the mapped API response so the UI immediately shows accurate data.
   * Returns the new notification's ID (string), or `null` on failure.
   */
  const sendToApi = useCallback(async (
    displayFields: Omit<PortalNotification, "id">,
    formData: CreateNotificationFormState,
    userId: string,
    isDraft: boolean = false,
  ): Promise<string | null> => {
    try {
      const api = createApiClient();
      const { audienceTargetType, targetValues } = buildAudienceTargeting(formData);
      
      const body = {
        type: formData.notifType,
        title: formData.title,
        message: formData.message,
        description: formData.message,
        displayUntilClosed: formData.displayUntilClosed,
        startDateTime: !formData.displayUntilClosed ? formData.startDate : undefined,
        endDateTime: !formData.displayUntilClosed ? formData.endDate : undefined,
        severity: SEVERITY_MAP[formData.priority] ?? "Medium",
        createdByUserId: userId,
        audienceTargetType,
        targetValues,
        relatedTicketId: formData.linkedTicket || undefined,
        includeActionButton: formData.includeActionButton,
        estimatedUserCount: formData.estimatedUserCount ?? estimateRecipients(formData),
        triggerTeamsNotification: formData.sendVia.teams,
        triggerBannerNotification: formData.sendVia.inApp,
        triggerEmailNotification: formData.sendVia.email,
        isDraft,
      };
      const clientTimezone = getTimezoneOffsetString();
      const res = await api.post<{ success: boolean; data: TktNotificationResponse }>(
        `/ticketing/notifications`,
        body,
        { params: { clientTimezone } }
      );
      if (res.data.success) {
        const enriched: PortalNotification = {
          ...mapApiResponse(res.data.data),
          ...displayFields,
          id: String(res.data.data.notificationId),
          backendId: res.data.data.notificationId,
        };
        setNotifications((prev) => [enriched, ...prev]);
        return enriched.id;
      }
      return null;
    } catch (err) {
      console.error("[useNotificationsComms] sendToApi failed:", err);
      return null;
    }
  }, []);

  /**
   * Update an existing API-backed notification.
   * Returns true on success.
   */
  const updateOnApi = useCallback(async (
    id: string,
    displayFields: Partial<Omit<PortalNotification, "id">>,
    formData: CreateNotificationFormState,
    userId: string,
    isDraft: boolean = false,
  ): Promise<boolean> => {
    const notif = notificationsRef.current.find((n) => n.id === id);
    if (!notif?.backendId) return false;
    try {
      const api = createApiClient();
      const clientTimestamp = getLocalTimestamp();
      const clientTimezone = getTimezoneOffsetString();
      const { audienceTargetType, targetValues } = buildAudienceTargeting(formData);
      
      const body = {
        type: formData.notifType,
        title: formData.title,
        message: formData.message,
        description: formData.message,
        displayUntilClosed: formData.displayUntilClosed,
        startDateTime: !formData.displayUntilClosed ? formData.startDate : undefined,
        endDateTime: !formData.displayUntilClosed ? formData.endDate : undefined,
        severity: SEVERITY_MAP[formData.priority] ?? "Medium",
        audienceTargetType,
        targetValues,
        relatedTicketId: formData.linkedTicket || undefined,
        includeActionButton: formData.includeActionButton,
        estimatedUserCount: formData.estimatedUserCount ?? estimateRecipients(formData),
        triggerTeamsNotification: formData.sendVia.teams,
        triggerBannerNotification: formData.sendVia.inApp,
        triggerEmailNotification: formData.sendVia.email,
        isDraft,
      };
      const res = await api.put<{ success: boolean; data: TktNotificationResponse }>(
        `/ticketing/notifications/${notif.backendId}?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`,
        body
      );
      if (res.data.success) {
        const updated: PortalNotification = {
          ...mapApiResponse(res.data.data),
          ...displayFields,
          id,
          backendId: notif.backendId,
        };
        setNotifications((prev) => prev.map((n) => (n.id === id ? updated : n)));
        return true;
      }
      return false;
    } catch (err) {
      console.error("[useNotificationsComms] updateOnApi failed:", err);
      return false;
    }
  }, []);

  return {
    // State
    notifications,
    filteredNotifications,
    messages,
    activeTab,
    readFilter,
    inputMessage,
    attachPending,
    tabCounts,
    unreadCount,
    tabs: TABS,
    isLoadingNotifications,
    // Setters
    setActiveTab,
    setReadFilter,
    setInputMessage,
    setAttachPending,
    // Handlers
    markAsRead,
    toggleReadStatus,
    deleteNotification,
    sendMessage,
    markAllRead,
    addNotification,
    updateNotification,
    // API methods
    sendToApi,
    updateOnApi,
    fetchAll,
  };
};

export default useNotificationsComms;
